import { Upload } from 'lucide-react';
import { useCallback, useState, useEffect } from 'react';
import FloatingInput from '../FloatingInput.jsx';
import SearchableSelect from '../SearchableSelect.jsx';
import { employeeService,API_URL } from '../../../services/api';
import toast from 'react-hot-toast';

// Calculate age from date of birth (returns {years, months})
const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    try {
        const birthDate = new Date(dateOfBirth);
        const today = new Date();
        
        let years = today.getFullYear() - birthDate.getFullYear();
        let months = today.getMonth() - birthDate.getMonth();
        
        if (months < 0 || (months === 0 && today.getDate() < birthDate.getDate())) {
            years--;
            months += 12;
        }
        
        if (today.getDate() < birthDate.getDate()) {
            months--;
            if (months < 0) {
                months += 12;
                years--;
            }
        }
        
        return { years, months };
    } catch (e) {
        return null;
    }
};

// List of all Indian states and union territories
const INDIAN_STATES = [
    { value: 'Andhra Pradesh', label: 'Andhra Pradesh' },
    { value: 'Arunachal Pradesh', label: 'Arunachal Pradesh' },
    { value: 'Assam', label: 'Assam' },
    { value: 'Bihar', label: 'Bihar' },
    { value: 'Chhattisgarh', label: 'Chhattisgarh' },
    { value: 'Goa', label: 'Goa' },
    { value: 'Gujarat', label: 'Gujarat' },
    { value: 'Haryana', label: 'Haryana' },
    { value: 'Himachal Pradesh', label: 'Himachal Pradesh' },
    { value: 'Jharkhand', label: 'Jharkhand' },
    { value: 'Karnataka', label: 'Karnataka' },
    { value: 'Kerala', label: 'Kerala' },
    { value: 'Madhya Pradesh', label: 'Madhya Pradesh' },
    { value: 'Maharashtra', label: 'Maharashtra' },
    { value: 'Manipur', label: 'Manipur' },
    { value: 'Meghalaya', label: 'Meghalaya' },
    { value: 'Mizoram', label: 'Mizoram' },
    { value: 'Nagaland', label: 'Nagaland' },
    { value: 'Odisha', label: 'Odisha' },
    { value: 'Punjab', label: 'Punjab' },
    { value: 'Rajasthan', label: 'Rajasthan' },
    { value: 'Sikkim', label: 'Sikkim' },
    { value: 'Tamil Nadu', label: 'Tamil Nadu' },
    { value: 'Telangana', label: 'Telangana' },
    { value: 'Tripura', label: 'Tripura' },
    { value: 'Uttar Pradesh', label: 'Uttar Pradesh' },
    { value: 'Uttarakhand', label: 'Uttarakhand' },
    { value: 'West Bengal', label: 'West Bengal' },
    { value: 'Andaman and Nicobar Islands', label: 'Andaman and Nicobar Islands' },
    { value: 'Chandigarh', label: 'Chandigarh' },
    { value: 'Dadra and Nagar Haveli and Daman and Diu', label: 'Dadra and Nagar Haveli and Daman and Diu' },
    { value: 'Delhi', label: 'Delhi' },
    { value: 'Jammu and Kashmir', label: 'Jammu and Kashmir' },
    { value: 'Ladakh', label: 'Ladakh' },
    { value: 'Lakshadweep', label: 'Lakshadweep' },
    { value: 'Puducherry', label: 'Puducherry' }
];

// List of family relationships for emergency contact
const FAMILY_RELATIONS = [
    { value: 'Father', label: 'Father' },
    { value: 'Mother', label: 'Mother' },
    { value: 'Spouse', label: 'Spouse' },
    { value: 'Son', label: 'Son' },
    { value: 'Daughter', label: 'Daughter' },
    { value: 'Brother', label: 'Brother' },
    { value: 'Sister', label: 'Sister' },
    { value: 'Grandfather', label: 'Grandfather' },
    { value: 'Grandmother', label: 'Grandmother' },
    { value: 'Uncle', label: 'Uncle' },
    { value: 'Aunt', label: 'Aunt' },
    { value: 'Cousin', label: 'Cousin' },
    { value: 'Father-in-law', label: 'Father-in-law' },
    { value: 'Mother-in-law', label: 'Mother-in-law' },
    { value: 'Brother-in-law', label: 'Brother-in-law' },   
    { value: 'Sister-in-law', label: 'Sister-in-law' },
    { value: 'Son-in-law', label: 'Son-in-law' },
    { value: 'Daughter-in-law', label: 'Daughter-in-law' },
    { value: 'Nephew', label: 'Nephew' },
    { value: 'Niece', label: 'Niece' }
];

// List of marital status options
const MARITAL_STATUS_OPTIONS = [
    { value: 'Single', label: 'Single' },
    { value: 'Married', label: 'Married' },
    { value: 'Widowed', label: 'Widowed' },
    { value: 'Divorced', label: 'Divorced' }
];

export function PersonalDetailsForm({
    employeeData,
    setEmployeeData,
    onSaveSection,
    loading,
    feedback,
    darkMode,
    onCancel,
    onNext,
    onPrevious,
    isFirstTab,
    isLastTab,
    isEmployeeView = false,
    hideButtons = false,
    readOnly = false,
    onSaveTrigger,
}) {
    // State for pincode validation errors
    const [pincodeErrors, setPincodeErrors] = useState({
        pincode: '',
        permanentPincode: ''
    });

    // State for photo upload
    const [photoUploading, setPhotoUploading] = useState(false);
    const [photoUploadSuccess, setPhotoUploadSuccess] = useState(false);
    const [photoKey, setPhotoKey] = useState(0); // Force re-render key
    const [pendingPhotoFile, setPendingPhotoFile] = useState(null);

    const [signedPhotoUrl, setSignedPhotoUrl] = useState(null);

    // State for photo modal
    const [photoModal, setPhotoModal] = useState({ 
        isOpen: false, 
        photo: null, 
        fileName: '',
        employeeName: '',
        employeeId: '',
        downloadFileName: '', // Store downloadFileName from documents table
        isDataUrl: false // Track if photo is a data URL (newly uploaded)
    });

    // State for form validation errors
    const [formErrors, setFormErrors] = useState({});
    
    // State for copy address functionality
    const [copyAddressChecked, setCopyAddressChecked] = useState(false);

    // Create form data by reading from the correct sources
    const formData = {
        ...employeeData,
        // Present Address - read from nested address object
        address: employeeData.address?.address || '',
        city: employeeData.address?.city || '',
        state: employeeData.address?.state || '',
        country: employeeData.address?.country || 'India',
        pincode: employeeData.address?.pincode || '',
        
        // Permanent Address - read from nested address object
        permanentAddress: employeeData.address?.permanentAddress || '',
        permanentCity: employeeData.address?.permanentCity || '',
        permanentState: employeeData.address?.permanentState || '',
        permanentCountry: employeeData.address?.permanentCountry || 'India',
        permanentPincode: employeeData.address?.permanentPincode || '',
        
        // Emergency Contact - read from flat fields
        emergencyContactName: employeeData.emergencyContactName || '',
        emergencyContactPhone: employeeData.emergencyContactPhone || '',
        emergencyContactRelation: employeeData.emergencyContactRelation || '',
        
        // Marital Status
        maritalStatus: employeeData.maritalStatus || '',
        
        // Father Name
        fatherName: employeeData.fatherName || '',
        
        // Other fields
        photo: employeeData.photo || null
    };

    // Monitor photo state changes for debugging
    useEffect(() => {
        console.log('Photo state changed:', {
            hasPhoto: !!formData.photo,
            photoType: formData.photo ? (formData.photo.startsWith('data:') ? 'base64' : 'file path') : 'none',
            photoLength: formData.photo ? formData.photo.length : 0
        });
    }, [formData.photo]);

    // Check if addresses are the same on component load
    useEffect(() => {
        if (formData.address && formData.permanentAddress && 
            formData.city && formData.permanentCity && 
            formData.state && formData.permanentState && 
            formData.pincode && formData.permanentPincode) {
            
            const addressesMatch = formData.address === formData.permanentAddress &&
                                 formData.city === formData.permanentCity &&
                                 formData.state === formData.permanentState &&
                                 formData.pincode === formData.permanentPincode;
            
            setCopyAddressChecked(addressesMatch);
        }
    }, [formData.address, formData.permanentAddress, formData.city, formData.permanentCity, formData.state, formData.permanentState, formData.pincode, formData.permanentPincode]);

    const handleInputChange = useCallback((e) => {
        const { name } = e.target;
        let { value } = e.target;
        
        // Enforce emergency contact phone as 10 digits max (prevent typing/paste beyond 10)
        if (name === 'emergencyContactPhone') {
            value = String(value || '').replace(/\D/g, '').slice(0, 10);
        }

        // Check if permanent address fields match present address (for auto-checking copy checkbox)
        if (name === 'permanentAddress' || name === 'permanentCity' || name === 'permanentState' || name === 'permanentPincode') {
            const presentAddress = formData.address;
            const presentCity = formData.city;
            const presentState = formData.state;
            const presentPincode = formData.pincode;
            
            // Get current permanent address values
            const currentPermanentAddress = name === 'permanentAddress' ? value : formData.permanentAddress;
            const currentPermanentCity = name === 'permanentCity' ? value : formData.permanentCity;
            const currentPermanentState = name === 'permanentState' ? value : formData.permanentState;
            const currentPermanentPincode = name === 'permanentPincode' ? value : formData.permanentPincode;
            
            // Check if all permanent fields match present fields
            if (currentPermanentAddress === presentAddress && 
                currentPermanentCity === presentCity && 
                currentPermanentState === presentState && 
                currentPermanentPincode === presentPincode) {
                setCopyAddressChecked(true);
                
                // Clear validation errors for permanent address fields when they match present address
                setFormErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.permanentAddress;
                    delete newErrors.permanentCity;
                    delete newErrors.permanentState;
                    delete newErrors.permanentPincode;
                    return newErrors;
                });
                
                // Show success toast for automatic copying
                toast.success('Address automatically copied!');
            } else {
                setCopyAddressChecked(false);
            }
        }
        
        // Validation for pincode fields - only allow 6 digits
        if (name === 'pincode' || name === 'permanentPincode') {
            // Only allow numeric input and limit to 6 digits
            const numericValue = value.replace(/\D/g, '').slice(0, 6);
            const finalValue = numericValue;
            
            // Real-time validation
            let errorMessage = '';
            if (value && !/^[0-9]+$/.test(value)) {
                errorMessage = 'Only numeric values are allowed';
            } else if (value && value.length < 6) {
                errorMessage = 'Pincode must be 6 digits';
            } else if (value && value.length > 6) {
                errorMessage = 'Pincode cannot exceed 6 digits';
            }
            
            setPincodeErrors(prev => ({
                ...prev,
                [name]: errorMessage
            }));
            
            if (name.includes('.')) {
                const [parent, child] = name.split('.');
                setEmployeeData(prev => ({
                    ...prev,
                    [parent]: {
                        ...prev[parent],
                        [child]: finalValue,
                    },
                }));
            } else {
                setEmployeeData(prev => ({
                    ...prev,
                    address: {
                        ...prev.address,
                        [name]: finalValue,
                    },
                }));
            }
            
            // Clear form validation error for this field
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
            
            return;
        }
        
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setEmployeeData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value,
                },
            }));
        } else {
            // Handle address fields by updating the address object
            if (['address', 'city', 'state', 'country', 'pincode', 
                 'permanentAddress', 'permanentCity', 'permanentState', 
                 'permanentCountry', 'permanentPincode'].includes(name)) {
                setEmployeeData(prev => ({
                    ...prev,
                    address: {
                        ...prev.address,
                        [name]: value,
                    },
                }));
            } else {
                // Handle all other fields including emergency contact fields
                setEmployeeData(prev => ({ ...prev, [name]: value }));
            }
        }
        
        // Real-time validation for non-pincode fields
        if (!['pincode', 'permanentPincode'].includes(name)) {
            let error = null;
            
            if (name === 'emergencyContactPhone') {
                error = validatePhone(value);
            } else if (['address', 'city', 'permanentAddress', 'permanentCity'].includes(name)) {
                error = validateRequiredField(value, name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()));
            } else if (['state', 'permanentState', 'emergencyContactRelation', 'maritalStatus'].includes(name)) {
                error = validateSelectField(value, name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()));
            } else if (['emergencyContactName'].includes(name)) {
                error = validateRequiredField(value, 'Emergency Contact Name');
            }
            
            // Update form errors
            setFormErrors(prev => {
                if (error) {
                    return { ...prev, [name]: error };
                } else {
                    const newErrors = { ...prev };
                    delete newErrors[name];
                    return newErrors;
                }
            });
        }
    }, [setEmployeeData]);

    // Separate handler for SearchableSelect components
    const handleSelectChange = useCallback((name, value) => {
        console.log('Select field changed:', name, value);
        
        // Update employee data
        if (['state', 'permanentState'].includes(name)) {
            setEmployeeData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    [name]: value,
                },
            }));
        } else {
            setEmployeeData(prev => ({ ...prev, [name]: value }));
        }
        
        // Validate the field
        let error = null;
        if (['state', 'permanentState'].includes(name)) {
            error = validateSelectField(value, name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()));
        } else if (name === 'emergencyContactRelation') {
            error = validateSelectField(value, 'Emergency Contact Relationship');
        } else if (name === 'maritalStatus') {
            error = validateSelectField(value, 'Marital Status');
        }
        
        // Update form errors
        setFormErrors(prev => {
            if (error) {
                return { ...prev, [name]: error };
            } else {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            }
        });
    }, [setEmployeeData]);

    const handlePhotoUpload = useCallback(async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                setPhotoUploading(true);

                // Defer backend upload until user clicks Update & Next.
                // Store selected file in state and set preview in employeeData.photo.
                setPendingPhotoFile(file);

                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64Photo = reader.result;
                    setEmployeeData(prev => ({
                        ...prev,
                        photo: base64Photo
                    }));

                    // Force re-render by updating photo key
                    setPhotoKey(prev => prev + 1);

                    setPhotoUploading(false);
                    setPhotoUploadSuccess(true);
                    setTimeout(() => setPhotoUploadSuccess(false), 3000);

                    // Clear photo validation error
                    setFormErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.photo;
                        return newErrors;
                    });
                };
                reader.readAsDataURL(file);
            } catch (error) {
                console.error('Error uploading photo:', error);
                toast.error('Error uploading photo: ' + (error.message || 'Unknown error'));
            } finally {
                setPhotoUploading(false);
            }
        }
    }, [setEmployeeData, employeeData.id]);

    useEffect(() => {
        let cancelled = false;

        async function loadSignedPhoto() {
            try {
                if (!employeeData?.id) {
                    setSignedPhotoUrl(null);
                    return;
                }

                const photoValue = formData.photo;
                if (!photoValue || (typeof photoValue === 'string' && photoValue.startsWith('data:'))) {
                    setSignedPhotoUrl(null);
                    return;
                }

                const resp = await employeeService.getEmployeePhotoUrl(employeeData.id);
                const url = resp?.data?.url || resp?.url || null;
                if (!cancelled) setSignedPhotoUrl(url);
            } catch (e) {
                if (!cancelled) setSignedPhotoUrl(null);
            }
        }

        loadSignedPhoto();
        return () => {
            cancelled = true;
        };
    }, [employeeData?.id, formData.photo]);

    // Photo modal handlers
    const handleViewPhoto = useCallback(async () => {
        const employeeName = employeeData?.user?.name || employeeData?.name || 'Employee';
        const empId = employeeData?.id || employeeData?.employeeId || 'Unknown';

        // Case 1: Newly uploaded photo (data URL)
        if (formData.photo && typeof formData.photo === 'string' && formData.photo.startsWith('data:')) {
            setPhotoModal({ 
                isOpen: true, 
                photo: formData.photo, 
                fileName: `${employeeName}`,
                employeeName: employeeName,
                employeeId: empId,
                isDataUrl: true
            });
            return;
        }

        // Case 2: Existing photo stored in backend
        if (formData.photo && employeeData?.id) {
            try {
                const resp = await employeeService.getEmployeePhotoUrl(employeeData.id);
                const url = resp?.data?.url || resp?.url || null;
                
                // Get downloadFileName by making a HEAD request or using download URL endpoint
                // Actually, we'll get it when downloading, so just use empCode for now
                const downloadFileName = employeeData?.empCode ? `${employeeData.empCode}.jpg` : '';
                
                setPhotoModal({ 
                    isOpen: true, 
                    photo: url, 
                    fileName: `${employeeName}`,
                    employeeName: employeeName,
                    employeeId: empId,
                    downloadFileName: downloadFileName,
                    isDataUrl: false
                });
            } catch (e) {
                console.error('Failed to load employee photo signed URL', e);
                setPhotoModal({ 
                    isOpen: true, 
                    photo: null, 
                    fileName: `${employeeName}`,
                    employeeName: employeeName,
                    employeeId: empId,
                    downloadFileName: employeeData?.empCode ? `${employeeData.empCode}.jpg` : '',
                    isDataUrl: false
                });
            }
            return;
        }

        // Case 3: No photo available
        setPhotoModal({ 
            isOpen: true, 
            photo: null, 
            fileName: `${employeeName}`,
            employeeName: employeeName,
            employeeId: empId,
            isDataUrl: false
        });
    }, [formData.photo, employeeData?.id, employeeData?.employeeId, employeeData?.user?.name, employeeData?.name]);

    const closePhotoModal = useCallback(() => {
        setPhotoModal({ 
            isOpen: false, 
            photo: null, 
            fileName: '', 
            employeeName: '',
            employeeId: '',
            isDataUrl: false
        });
    }, []);

    // Helper function to generate sanitized filename
    const generateFileName = useCallback((employeeName, employeeId, suffix = '', extension = 'jpg') => {
        const sanitizeName = (name) => (name || 'Employee').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
        const sanitizedName = sanitizeName(employeeName);
        const empId = employeeId || 'Unknown';
        return suffix ? `${sanitizedName}_${empId}_${suffix}.${extension}` : `${sanitizedName}_${empId}.${extension}`;
    }, []);

    // Helper function to convert data URL to blob
    const dataUrlToBlob = useCallback(async (dataUrl) => {
        const response = await fetch(dataUrl);
        return await response.blob();
    }, []);

    // Helper function to trigger download
    const triggerDownload = useCallback((blob, fileName) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, []);

    const downloadPhoto = useCallback(async () => {
        if (!photoModal.photo && !employeeData?.id) return;
        
        try {
            let blob;
            let extension = 'jpg';
            
            // Case 1: Data URL (newly uploaded photo, not yet saved to backend)
            if (photoModal.isDataUrl && photoModal.photo) {
                const match = photoModal.photo.match(/data:image\/(\w+);/);
                extension = (match && match[1]) || 'png';
                blob = await dataUrlToBlob(photoModal.photo);
                const fileName = generateFileName(photoModal.employeeName, photoModal.employeeId, '', extension);
                triggerDownload(blob, fileName);
                toast.success('Photo downloaded successfully');
            } 
            // Case 2: Existing photo stored in backend - use backend proxy to avoid CORS
            else if (employeeData?.id) {
                try {
                    // Fetch through backend proxy (no CORS issues)
                    const { blob: photoBlob, downloadFileName } = await employeeService.getEmployeePhotoBlobWithFilename(employeeData.id);
                    blob = photoBlob;
                    
                    // Use downloadFileName from response, fallback to modal state or generate from empCode
                    let fileName = downloadFileName;
                    if (!fileName && employeeData?.empCode) {
                        // Extract extension from blob type
                        if (blob.type) {
                            const typeMatch = blob.type.match(/image\/(\w+)/);
                            if (typeMatch) extension = typeMatch[1] === 'jpeg' ? 'jpg' : typeMatch[1];
                        }
                        fileName = `${employeeData.empCode}.${extension}`;
                    } else if (!fileName) {
                        // Fallback to generated name if no downloadFileName available
                        if (blob.type) {
                            const typeMatch = blob.type.match(/image\/(\w+)/);
                            if (typeMatch) extension = typeMatch[1] === 'jpeg' ? 'jpg' : typeMatch[1];
                        }
                        fileName = generateFileName(photoModal.employeeName, photoModal.employeeId, '', extension);
                    }
                    
                    triggerDownload(blob, fileName);
                    toast.success('Photo downloaded successfully');
                    return;
                } catch (apiError) {
                    console.error('Backend proxy failed:', apiError);
                    toast.error('Failed to download photo. Please try again or contact support.');
                    return;
                }
            } else {
                toast.error('Photo not available for download');
                return;
            }
        } catch (error) {
            console.error('Error downloading photo:', error);
            toast.error('Failed to download photo: ' + (error.message || 'Unknown error'));
        }
    }, [photoModal, employeeData?.id, employeeData?.empCode, dataUrlToBlob, generateFileName, triggerDownload]);

    const downloadCompressedPhoto = useCallback(async () => {
        if (!photoModal.photo && !employeeData?.id) return;
        
        const loadingToast = toast.loading('Compressing image...');
        
        try {
            let img = new Image();
            let downloadFileName = ''; // Store downloadFileName for compressed filename
            
            // Case 1: Data URL (newly uploaded photo)
            if (photoModal.isDataUrl && photoModal.photo) {
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                    img.src = photoModal.photo;
                });
            } 
            // Case 2: Existing photo - fetch from backend proxy and convert to data URL
            else if (employeeData?.id) {
                try {
                    // Fetch blob from backend (no CORS issues)
                    const { blob: photoBlob, downloadFileName: filenameFromResponse } = await employeeService.getEmployeePhotoBlobWithFilename(employeeData.id);
                    downloadFileName = filenameFromResponse;
                    
                    // Convert blob to data URL for image loading
                    const dataUrl = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(photoBlob);
                    });
                    
                    // Load into image element
                    await new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = reject;
                        img.src = dataUrl;
                    });
                } catch (apiError) {
                    console.error('Backend proxy failed:', apiError);
                    toast.dismiss(loadingToast);
                    toast.error('Failed to load image for compression. Please try again.');
                    return;
                }
            } else {
                toast.dismiss(loadingToast);
                toast.error('Photo not available for compression');
                return;
            }
            
            // Get original dimensions
            let width = img.naturalWidth || img.width;
            let height = img.naturalHeight || img.height;
            
            if (!width || !height) {
                toast.dismiss(loadingToast);
                toast.error('Failed to load image dimensions');
                return;
            }
            
            const maxDimension = 1920; // Max width or height
            
            // Scale down if too large
            if (width > maxDimension || height > maxDimension) {
                const ratio = Math.min(maxDimension / width, maxDimension / height);
                width = Math.floor(width * ratio);
                height = Math.floor(height * ratio);
            }
            
            // Create canvas and draw image
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            // Compress to between 50KB and 100KB (strict)
            const MIN_SIZE = 50 * 1024;
            const MAX_SIZE = 100 * 1024;

            const toBlob = (q) =>
                new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));

            let blob = null;
            let attempts = 0;
            const maxAttempts = 8; // dimension-reduction attempts

            while (attempts < maxAttempts) {
                // If even near-lossless is < 50KB, we can't "compress" into the range.
                const maxQBlob = await toBlob(0.95);
                if (!maxQBlob) break;
                if (maxQBlob.size < MIN_SIZE) {
                    toast.dismiss(loadingToast);
                    toast.error('Cannot compress into 50–100KB (image is already too small).', { position: 'top-right' });
                    return;
                }
                if (maxQBlob.size <= MAX_SIZE) {
                    blob = maxQBlob; // already within range
                    break;
                }

                // Binary search quality to land within range
                let lo = 0.1;
                let hi = 0.95;
                let best = null;
                for (let i = 0; i < 10; i++) {
                    const mid = (lo + hi) / 2;
                    const b = await toBlob(mid);
                    if (!b) break;
                    if (b.size > MAX_SIZE) {
                        hi = mid; // need more compression (lower quality)
                    } else if (b.size < MIN_SIZE) {
                        lo = mid; // too small, increase quality
                    } else {
                        best = b;
                        break;
                    }
                }

                if (best) {
                    blob = best;
                    break;
                }

                // If we couldn't hit the range at this dimension, reduce dimensions and retry.
                width = Math.floor(width * 0.9);
                height = Math.floor(height * 0.9);
                if (width < 50 || height < 50) break;
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);
                attempts++;
            }
            
            if (!blob) {
                toast.dismiss(loadingToast);
                toast.error('Failed to compress image into 50–100KB.');
                return;
            }
            
            // Generate compressed filename from downloadFileName
            let fileName = '';
            if (downloadFileName) {
                // Extract base name and extension, then add _compressed before extension
                const lastDotIndex = downloadFileName.lastIndexOf('.');
                if (lastDotIndex !== -1) {
                    const baseName = downloadFileName.substring(0, lastDotIndex);
                    const ext = downloadFileName.substring(lastDotIndex + 1);
                    fileName = `${baseName}_compressed.${ext}`;
                } else {
                    fileName = `${downloadFileName}_compressed.jpg`;
                }
            } else if (employeeData?.empCode) {
                // Fallback to empCode if downloadFileName not available
                fileName = `${employeeData.empCode}_compressed.jpg`;
            } else {
                // Final fallback to generated name
                fileName = generateFileName(photoModal.employeeName, photoModal.employeeId, 'compressed', 'jpg');
            }
            
            triggerDownload(blob, fileName);
            
            toast.dismiss(loadingToast);
            toast.success(`Image compressed to ${(blob.size / 1024).toFixed(2)}KB and downloaded`);
        } catch (error) {
            console.error('Error compressing and downloading photo:', error);
            toast.dismiss(loadingToast);
            toast.error('Failed to compress and download photo: ' + (error.message || 'Unknown error'));
        }
    }, [photoModal, employeeData?.id, employeeData?.empCode, generateFileName, triggerDownload]);

    // Validation function for pincode
    const validatePincode = (pincode) => {
        if (!pincode) return true; // Allow empty pincode
        return /^[0-9]{6}$/.test(pincode);
    };

    // Validation function for phone number
    const validatePhone = (phone) => {
        if (!phone) return 'Phone number is required';
        const phoneRegex = /^[6-9]\d{9}$/;
        if (!phoneRegex.test(phone)) {
            return 'Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9';
        }
        return null;
    };

    // Validation function for text fields
    const validateRequiredField = (value, fieldName) => {
        if (!value || value.trim() === '') {
            return `${fieldName} is required`;
        }
        if (value.trim().length < 2) {
            return `${fieldName} must be at least 2 characters long`;
        }
        return null;
    };

    // Validation function for dropdown/select fields
    const validateSelectField = (value, fieldName) => {
        if (!value || value.trim() === '') {
            return `${fieldName} is required`;
        }
        return null;
    };

    const validateName = (name , fieldName) => {
        if (!name || name.trim() === '') return `${fieldName} is required`;
        if (name.trim().length < 2) {
          return `${fieldName} must be at least 2 characters long`;
        }
        if (name.length > 50) {
          return `${fieldName} cannot exceed 50 characters`;
        }
        const nameRegex = /^[a-zA-Z\s]+$/;
        if (!nameRegex.test(name)) {
          return `${fieldName} can only contain letters and spaces`;
        }
        return null;
      };
      
    // Comprehensive form validation
    const validateForm = () => {
        const errors = {};
        
        // Validate photo
        if (!formData.photo) {
            errors.photo = 'Employee photo is required';
        }
        
        // Validate present address fields
        errors.address = validateRequiredField(formData.address, 'Present Address');
        errors.city = validateRequiredField(formData.city, 'Present City');
        errors.state = validateSelectField(formData.state, 'Present State');
        errors.pincode = validateRequiredField(formData.pincode, 'Present Pincode');
        
        // Validate permanent address fields
        errors.permanentAddress = validateRequiredField(formData.permanentAddress, 'Permanent Address');
        errors.permanentCity = validateRequiredField(formData.permanentCity, 'Permanent City');
        errors.permanentState = validateSelectField(formData.permanentState, 'Permanent State');
        errors.permanentPincode = validateRequiredField(formData.permanentPincode, 'Permanent Pincode');
        
        // Validate emergency contact fields
        errors.emergencyContactName = validateRequiredField(formData.emergencyContactName, 'Emergency Contact Name');
        errors.emergencyContactPhone = validatePhone(formData.emergencyContactPhone);
        errors.emergencyContactRelation = validateSelectField(formData.emergencyContactRelation, 'Emergency Contact Relationship');
        
        // Validate marital status
        errors.maritalStatus = validateSelectField(formData.maritalStatus, 'Marital Status');
        errors.fatherName = validateRequiredField(formData.fatherName, 'Father\'s Name');
        errors.fatherName = validateName(formData.fatherName, 'Father\'s Name');
        errors.emergencyContactName = validateName(formData.emergencyContactName, 'Emergency Contact Name');
        
        console.log('Validation debug:', {
            presentState: formData.state,
            permanentState: formData.permanentState,
            emergencyRelation: formData.emergencyContactRelation,
            maritalStatus: formData.maritalStatus,
            stateError: errors.state,
            permanentStateError: errors.permanentState,
            relationError: errors.emergencyContactRelation,
            maritalStatusError: errors.maritalStatus
        });
        
        // Remove null values (valid fields)
        Object.keys(errors).forEach(key => {
            if (errors[key] === null) {
                delete errors[key];
            }
        });
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const getErrorMessage = (error) => {
        return (
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            'Failed to save. Please try again.'
        );
    };

    const handleSave = async () => {
        // Validate the entire form before proceeding
        if (!validateForm()) {
            console.log('Form validation failed:', formErrors);
            toast.error('Please fix the validation errors before saving.', { position: 'top-right' });
            return { saved: false }; // Don't save if there are validation errors
        }
        
        // Check if there are any pincode errors
        if (pincodeErrors.pincode || pincodeErrors.permanentPincode) {
            toast.error('Please fix the pincode errors before saving.', { position: 'top-right' });
            return { saved: false }; // Don't save if there are pincode validation errors
        }
        
        // Validate pincodes before saving
        const presentPincodeValid = validatePincode(formData.pincode);
        const permanentPincodeValid = validatePincode(formData.permanentPincode);
        
        if (!presentPincodeValid || !permanentPincodeValid) {
            toast.error('Please enter valid 6-digit pincodes before saving.', { position: 'top-right' });
            return { saved: false };
        }
        
        console.log('Form validation passed - proceeding with save...');

        // Upload photo only when user clicks Update & Next (save flow)
        let resolvedPhotoPath =
            formData.photo && typeof formData.photo === 'string' && !formData.photo.startsWith('data:')
                ? formData.photo
                : null;

        if (pendingPhotoFile && employeeData?.id) {
            try {
                setPhotoUploading(true);
                const response = await employeeService.uploadEmployeePhoto(employeeData.id, pendingPhotoFile);
                if (!response?.success || !response?.photoPath) {
                    toast.error('Photo upload failed: ' + (response?.message || 'Unknown error'));
                    setPhotoUploading(false);
                    return { saved: false };
                }

                resolvedPhotoPath = response.photoPath;
                setEmployeeData(prev => ({
                    ...prev,
                    photo: response.photoPath,
                }));

                setPendingPhotoFile(null);
                setPhotoKey(prev => prev + 1);

                // Clear signed URL cache (it will reload based on new path)
                setSignedPhotoUrl(null);
                toast.success('Photo uploaded successfully');
            } catch (e) {
                toast.error('Error uploading photo: ' + (e?.message || 'Unknown error'));
                setPhotoUploading(false);
                return { saved: false };
            } finally {
                setPhotoUploading(false);
            }
        }
        
        // Prepare data matching backend API expectations
        const sectionData = {
            // Photo - only send file path, not base64 data
            photo: resolvedPhotoPath,
            
            // Emergency Contact (matching backend field names)
            emergencyContactName: formData.emergencyContactName || '',
            emergencyContactPhone: formData.emergencyContactPhone || '',
            emergencyContactRelation: formData.emergencyContactRelation || '',

            // Father's Name
            fatherName: formData.fatherName || '',
            
            // Marital Status
            maritalStatus: formData.maritalStatus || '',
            
            // Present Address (matching backend field names)
            address: formData.address || '',
            city: formData.city || '',
            state: formData.state || '',
            country: formData.country || 'India',
            pincode: formData.pincode || '',
            
            // Permanent Address (matching backend field names)
            permanentAddress: formData.permanentAddress || '',
            permanentCity: formData.permanentCity || '',
            permanentState: formData.permanentState || '',
            permanentCountry: formData.permanentCountry || 'India',
            permanentPincode: formData.permanentPincode || ''
        };
        
        console.log('Personal Details being sent to backend:', sectionData);
        
        try {
            await onSaveSection(sectionData);
            toast.success('Personal details saved successfully', { position: 'top-right' });
            // Navigate to next tab after successful save (for both admin and employee views)
            if (onNext && !isLastTab) {
                onNext();
            }
            return { saved: true };
        } catch (error) {
            console.error('Save failed:', error);
            toast.error(getErrorMessage(error), { position: 'top-right' });
            throw error; // Stay on current tab / prevent navigation
        }
    };

    useEffect(() => {
        if (typeof onSaveTrigger === 'function') {
            onSaveTrigger(handleSave);
        }
    }, [onSaveTrigger, handleSave]);

    return (
        <div className="space-y-2">
            {/* Top Navigation Buttons - Keep in DOM but hidden when hideButtons is true */}
            <div className={`sticky top-0 z-5 flex justify-end mb-4 bg-white py-2 shadow-sm ${hideButtons ? 'hidden' : ''}`}>
                <div className="flex space-x-2">
                    {!isFirstTab && (
                        <button
                            onClick={onPrevious}
                            className="px-3 py-1.5 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-sm"
                            disabled={loading}
                        >
                            Previous
                        </button>
                    )}
                    <button
                        onClick={onCancel}
                        className="px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 text-sm"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    {!isLastTab ? (
                        <button
                            onClick={handleSave}
                            className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm"
                            disabled={loading || readOnly}
                        >
                            Next
                        </button>
                    ) : (
                        <button
                            onClick={handleSave}
                            className={`px-3 py-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 text-sm ${
                                isEmployeeView
                                    ? 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500'
                                    : 'bg-green-500 text-white hover:bg-green-600 focus:ring-green-500'
                            }`}
                            disabled={loading || readOnly}
                        >
                            {isEmployeeView ? 'Next' : (employeeData.id ? 'Update' : 'Save')}
                        </button>
                    )}
                </div>
            </div>
            <fieldset disabled={readOnly} className={readOnly ? 'opacity-75' : ''}>
            <div className="flex items-center space-x-3">
                <div 
                    className={`w-24 h-24 bg-gray-200 rounded-full overflow-hidden relative ${formData.photo && (formData.photo.startsWith('data:') || formData.photo.length > 0) ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
                    onClick={formData.photo && (formData.photo.startsWith('data:') || formData.photo.length > 0) ? handleViewPhoto : undefined}
                    title={formData.photo && (formData.photo.startsWith('data:') || formData.photo.length > 0) ? 'Click to view photo' : ''}
                >
                    {formData.photo && (formData.photo.startsWith('data:') || formData.photo.length > 0) ? (
                        <>
                            <img 
                                key={`photo-${photoKey}-${formData.photo ? (formData.photo.startsWith('data:') ? formData.photo.substring(0, 100) : formData.photo) : 'no-photo'}`}
                                src={
                                    formData.photo.startsWith('data:')
                                        ? formData.photo
                                        : (signedPhotoUrl || '')
                                }
                                alt="Employee" 
                                className="w-full h-full object-cover"
                                onLoad={(e) => {
                                    console.log('Image loaded successfully');
                                    // Hide fallback when image loads successfully
                                    const fallback = e.target.parentElement.querySelector('.photo-fallback');
                                    if (fallback) {
                                        fallback.style.display = 'none';
                                    }
                                    // Show the image
                                    e.target.style.display = 'block';
                                }}
                                onError={(e) => {
                                    console.log('Image failed to load:', e.target.src);
                                    // Hide image and show fallback on error
                                    e.target.style.display = 'none';
                                    const fallback = e.target.parentElement.querySelector('.photo-fallback');
                                    if (fallback) {
                                        fallback.style.display = 'flex';
                                    }
                                }}
                                style={{ display: 'block' }} // Ensure image is visible by default
                            />
                            <div className="photo-fallback w-full h-full flex items-center justify-center text-gray-400 absolute inset-0" style={{ display: 'none' }}>
                                No Photo
                            </div>
                        </>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                            No Photo
                        </div>
                    )}
                </div>
                <div>
                    <label
                        htmlFor="photo-upload"
                        className={`cursor-pointer px-4 py-2 rounded text-sm flex items-center ${
                            photoUploading 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-blue-500 hover:bg-blue-600'
                        } text-white`}
                    >
                        {photoUploading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Uploading...
                            </>
                        ) : (
                            <>
                                <Upload className="mr-2" size={16} />
                                {formData.photo ? 'Upload New Photo' : 'Upload Photo *'}
                            </>
                        )}
                    </label>
                    <input
                        id="photo-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handlePhotoUpload}
                        disabled={photoUploading}
                        required
                    />
                </div>
            </div>
            
            {/* Photo validation error */}
            {formErrors.photo && (
                <div className="text-red-500 text-sm mt-1">{formErrors.photo}</div>
            )}
            
            {/* Photo upload success message */}
            {photoUploadSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-green-800">
                                Photo selected successfully!
                            </p>
                        </div>
                    </div>
                </div>
            )}

             {/* Marital Status Section */}
             <div className="space-y-1">
                {/* <h3 className="font-semibold text-lg">Personal Information</h3> */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="space-y-1"> 
                        <FloatingInput
                            name="fatherName"
                            label="Father's Name"
                            value={formData.fatherName || ''}
                            onChange={handleInputChange}
                            placeholder="Father's Name"
                            required
                        />
                        {formErrors.fatherName && (
                            <div className="text-red-500 text-sm">{formErrors.fatherName}</div>
                        )}
                    </div>
                    <div className="space-y-1"> 
                        
                        <SearchableSelect
                            id="maritalStatus"
                            label="Marital Status"
                            value={formData.maritalStatus || ''}
                            onChange={(e) => handleSelectChange('maritalStatus', e.target.value)}
                            staticOptions={MARITAL_STATUS_OPTIONS}
                            darkMode={darkMode}
                            required
                        />
                        
                        {formErrors.maritalStatus && (
                            <div className="text-red-500 text-sm">{formErrors.maritalStatus}</div>
                        )}

                    </div>
                    {/* Age Display */}
                    {employeeData?.user?.dateOfBirth && calculateAge(employeeData.user.dateOfBirth) !== null && (() => {
                        const age = calculateAge(employeeData.user.dateOfBirth);
                        return (
                            <div className="space-y-1">
                                <div className="flex items-center space-x-2">
                                    <div className="bg-blue-50 border border-blue-200 rounded-none px-2 py-1.5 flex items-center space-x-2">
                                        <span className="text-blue-600 font-semibold text-sm">
                                            Age:
                                        </span>
                                        <span className="text-blue-800 font-bold text-base">
                                            {age.years}
                                        </span>
                                        <span className="text-blue-600 text-xs">
                                            {age.years === 1 ? 'year' : 'years'}
                                        </span>
                                        {age.months > 0 && (
                                            <>
                                                <span className="text-blue-800 font-bold text-base">
                                                    {age.months}
                                                </span>
                                                <span className="text-blue-600 text-xs">
                                                    {age.months === 1 ? 'month' : 'months'}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>
            
            <div className="space-y-1">
                <h3 className="font-semibold text-lg mb-2">Present Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="space-y-1">
                        <FloatingInput
                            name="address"
                            label="House Number/Street/Flat"
                            value={formData.address || ''}
                            onChange={handleInputChange}
                            placeholder="House Number/Street/Flat"
                            required
                        />
                        {formErrors.address && (
                            <div className="text-red-500 text-sm">{formErrors.address}</div>
                        )}
                    </div>
                    <div className="space-y-1">
                        <FloatingInput
                            name="city"
                            label="City"
                            value={formData.city || ''}
                            onChange={handleInputChange}
                            placeholder="City"
                            required
                        />
                        {formErrors.city && (
                            <div className="text-red-500 text-sm">{formErrors.city}</div>
                        )}
                    </div>
                    <div className="space-y-1">
                        <SearchableSelect
                            id="state"
                            label="State"
                            value={formData.state || ''}
                            onChange={(e) => handleSelectChange('state', e.target.value)}
                            staticOptions={INDIAN_STATES}
                            darkMode={darkMode}
                            required
                        />
                        {formErrors.state && (
                            <div className="text-red-500 text-sm">{formErrors.state}</div>
                        )}
                    </div>
                    <div>
                        <FloatingInput
                            name="country"
                            label="Country"
                            value={formData.country || 'India'}
                            onChange={handleInputChange}
                            disabled={true}
                        />
                    </div>
                    <div className="space-y-1">
                        <FloatingInput
                            name="pincode"
                            label="Pincode"
                            value={formData.pincode || ''}
                            onChange={handleInputChange}
                            type="text"
                            placeholder="Enter 6-digit pincode"
                            maxLength={6}
                            pattern="[0-9]{6}"
                            title="Please enter a 6-digit pincode"
                            required
                        />
                        {formErrors.pincode && (
                            <div className="text-red-500 text-sm">{formErrors.pincode}</div>
                        )}
                        {pincodeErrors.pincode && (
                            <div className="text-red-500 text-sm">{pincodeErrors.pincode}</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Copy Address Checkbox */}
            <div className={`flex items-center space-x-2 p-2 rounded-lg border transition-colors duration-200 ${
                copyAddressChecked 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-gray-50 border-gray-200'
            }`}>
                <input
                    type="checkbox"
                    id="copyAddress"
                    name="copyAddress"
                    checked={copyAddressChecked}
                    onChange={(e) => {
                        const isChecked = e.target.checked;
                        setCopyAddressChecked(isChecked);
                        
                        if (isChecked) {
                            // Copy present address to permanent address
                            setEmployeeData(prev => ({
                                ...prev,
                                address: {
                                    ...prev.address,
                                    permanentAddress: formData.address,
                                    permanentCity: formData.city,
                                    permanentState: formData.state,
                                    permanentCountry: formData.country,
                                    permanentPincode: formData.pincode
                                }
                            }));
                            
                            // Clear validation errors for permanent address fields when copying
                            setFormErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors.permanentAddress;
                                delete newErrors.permanentCity;
                                delete newErrors.permanentState;
                                delete newErrors.permanentPincode;
                                return newErrors;
                            });
                            
                            // Show success toast
                            toast.success('Address copied successfully!');
                        } else {
                            // Clear permanent address fields when unchecked
                            setEmployeeData(prev => ({
                                ...prev,
                                address: {
                                    ...prev.address,
                                    permanentAddress: '',
                                    permanentCity: '',
                                    permanentState: '',
                                    permanentCountry: 'India',
                                    permanentPincode: ''
                                }
                            }));
                        }
                    }}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <label htmlFor="copyAddress" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Same as Present Address
                </label>
                <span className={`text-xs ml-2 ${
                    copyAddressChecked ? 'text-green-600' : 'text-gray-500'
                }`}>
                    {copyAddressChecked 
                        ? '✓ Addresses copied successfully!' 
                        : '(Check this box to copy present address details to permanent address)'
                    }
                </span>
            </div>

            {/* Permanent Address Section */}
            <div className="space-y-1">
                <h3 className="font-semibold text-lg mb-2">Permanent Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="space-y-1">
                        <FloatingInput
                            name="permanentAddress"
                            label="House Number/Street/Flat"
                            value={formData.permanentAddress || ''}
                            onChange={handleInputChange}
                            placeholder="House Number/Street/Flat"
                            required
                        />
                        {formErrors.permanentAddress && (
                            <div className="text-red-500 text-sm">{formErrors.permanentAddress}</div>
                        )}
                    </div>
                    <div className="space-y-1">
                        <FloatingInput
                            name="permanentCity"
                            label="City"
                            value={formData.permanentCity || ''}
                            onChange={handleInputChange}
                            placeholder="City"
                            required
                        />
                        {formErrors.permanentCity && (
                            <div className="text-red-500 text-sm">{formErrors.permanentCity}</div>
                        )}
                    </div>
                    <div className="space-y-1">
                        <SearchableSelect
                            id="permanentState"
                            label="State"
                            value={formData.permanentState || ''}
                            onChange={(e) => handleSelectChange('permanentState', e.target.value)}
                            staticOptions={INDIAN_STATES}
                            darkMode={darkMode}
                            required
                        />
                        {formErrors.permanentState && (
                            <div className="text-red-500 text-sm">{formErrors.permanentState}</div>
                        )}
                    </div>
                    <div>
                        <FloatingInput
                            name="permanentCountry"
                            label="Country"
                            value={formData.permanentCountry || 'India'}
                            onChange={handleInputChange}
                            disabled={true}
                        />
                    </div>
                    <div className="space-y-1">
                        <FloatingInput
                            name="permanentPincode"
                            label="Pincode"
                            value={formData.permanentPincode || ''}
                            onChange={handleInputChange}
                            type="text"
                            placeholder="Enter 6-digit pincode"
                            maxLength={6}
                            pattern="[0-9]{6}"
                            title="Please enter a 6-digit pincode"
                            required
                        />
                        {formErrors.permanentPincode && (
                            <div className="text-red-500 text-sm">{formErrors.permanentPincode}</div>
                        )}
                        {pincodeErrors.permanentPincode && (
                            <div className="text-red-500 text-sm">{pincodeErrors.permanentPincode}</div>
                        )}
                    </div>
                </div>
            </div>
            <div className="space-y-1">
                <h3 className="font-semibold text-lg mb-2">Emergency Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                        <FloatingInput
                            name="emergencyContactName"
                            label="Name"
                            value={formData.emergencyContactName || ''}
                            onChange={handleInputChange}
                            placeholder="Emergency Contact Name"
                            required
                        />
                        {formErrors.emergencyContactName && (
                            <div className="text-red-500 text-sm">{formErrors.emergencyContactName}</div>
                        )}
                    </div>
                    <div className="space-y-1">
                        <FloatingInput
                            name="emergencyContactPhone"
                            label="Number"
                            value={formData.emergencyContactPhone || ''}
                            onChange={handleInputChange}
                            placeholder="Emergency Contact Number"
                            type="text"
                            maxLength={10}
                            inputMode="numeric"
                            pattern="[0-9]{10}"
                            required
                        />
                        {formErrors.emergencyContactPhone && (
                            <div className="text-red-500 text-sm">{formErrors.emergencyContactPhone}</div>
                        )}
                    </div>
                    <div className="space-y-1">
                        <SearchableSelect
                            id="emergencyContactRelation"
                            label="Relationship"
                            value={formData.emergencyContactRelation || ''}
                            onChange={(e) => handleSelectChange('emergencyContactRelation', e.target.value)}
                            staticOptions={FAMILY_RELATIONS}
                            darkMode={darkMode}
                            required
                        />
                        {formErrors.emergencyContactRelation && (
                            <div className="text-red-500 text-sm">{formErrors.emergencyContactRelation}</div>
                        )}
                    </div>
                </div>
            </div>
            {feedback && (
                <div className={`mt-2 ${feedback.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>{feedback.message}</div>
            )}
            
            {/* Validation Summary - Show when form has validation errors */}
            {/* {Object.keys(formErrors).length > 0 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <h3 className="text-sm font-medium text-red-800 mb-2">Please fix the following errors before proceeding:</h3>
                    <ul className="text-sm text-red-700 space-y-1">
                        {Object.entries(formErrors).map(([field, error]) => (
                            <li key={field} className="flex items-start">
                                <span className="mr-2">•</span>
                                 <span>{error}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )} */}

            </fieldset>

            {/* Photo Modal */}
            {photoModal.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl max-h-[90vh] overflow-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {photoModal.fileName || 'Employee Photo'}
                            </h3>
                            <button
                                onClick={closePhotoModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="flex justify-center mb-4">
                            {photoModal.photo ? (
                                <img
                                    src={photoModal.photo}
                                    alt="Employee Photo"
                                    className="photo-modal-image max-w-full max-h-96 object-contain rounded-lg"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            ) : (
                                <div className="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-24 h-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex justify-center space-x-4">
                            {photoModal.photo && (
                                <>
                                    <button
                                        onClick={downloadPhoto}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center space-x-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span>Original</span>
                                    </button>
                                    <button
                                        onClick={downloadCompressedPhoto}
                                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center space-x-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                        <span>Compressed</span>
                                    </button>
                                </>
                            )}
                            <button
                                onClick={closePhotoModal}
                                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}